import { IconBuilding, IconUser } from '@tabler/icons-react'

import type { UserAccountContextItem } from '@/api/v1/auth/get-user-account-contexts'
import { cn } from '@/lib/utils'
import { useThemeStore } from '@/stores/theme'

type AccountLogoAccount = Pick<
  UserAccountContextItem,
  'accountName' | 'accountType' | 'squareLightLogoUrl' | 'squareDarkLogoUrl'
>

interface AccountLogoProps {
  account: AccountLogoAccount
  size?: number
  className?: string
}

/**
 * Square avatar for an account in switcher lists.
 *
 * Accounts that are not white-label — or that inherit white-label from their
 * managing account — have no square logo of their own. Those fall back to a
 * neutral placeholder icon rather than the Signater mark, which would read as
 * if the account itself were Signater.
 */
export function AccountLogo({
  account,
  size = 32,
  className,
}: AccountLogoProps) {
  const theme = useThemeStore((state) => state.theme)

  const logoUrl =
    theme === 'dark' ? account.squareDarkLogoUrl : account.squareLightLogoUrl

  if (logoUrl) {
    return (
      <img
        src={logoUrl}
        alt={account.accountName}
        className={cn('shrink-0 object-contain', className)}
        style={{ width: size, height: size }}
      />
    )
  }

  const PlaceholderIcon =
    account.accountType === 'Personal' ? IconUser : IconBuilding

  return (
    <div
      className={cn(
        // `--muted` and `--accent` resolve to the same value, so the fill alone
        // disappears on hovered rows; the border keeps the shape readable.
        'bg-muted text-muted-foreground flex shrink-0 items-center justify-center rounded-md border',
        className,
      )}
      style={{ width: size, height: size }}
    >
      <PlaceholderIcon size={Math.round(size * 0.6)} aria-hidden="true" />
    </div>
  )
}
